import { AutoLabelBadge } from '@/components/AutoLabelBadge';
import { cn } from '@/lib/utils';

export interface LabelFilterOption {
  id: string;
  name: string;
  color?: string | null;
}

interface LabelFilterBarProps {
  labels: LabelFilterOption[];
  activeLabelId: string | 'all';
  onSelect: (value: string | 'all') => void;
  noLabelOption?: LabelFilterOption | null;
}

export function LabelFilterBar({
  labels,
  activeLabelId,
  onSelect,
  noLabelOption = null,
}: LabelFilterBarProps) {
  // Find active label for mobile display
  const activeLabel = activeLabelId === 'all'
    ? null
    : activeLabelId === noLabelOption?.id
      ? noLabelOption
      : labels.find((l) => l.id === activeLabelId);

  const renderFilterButton = (labelId: string, labelName: string, color?: string | null) => {
    const isActive = activeLabelId === labelId;
    const buttonClass = cn(
      'inline-flex items-center rounded-full border px-0.5 py-0.5 transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40',
      isActive
        ? 'border-primary/60 border-2 bg-primary/10 shadow-sm'
        : 'border-transparent hover:bg-muted/60 hover:opacity-100 opacity-70'
    );
    return (
      <button
        key={labelId}
        type="button"
        className={buttonClass}
        onClick={() => onSelect(labelId)}
      >
        <AutoLabelBadge
          label={{ id: labelId, name: labelName, color: color ?? null }}
          className=""
        />
      </button>
    );
  };

  return (
    <>
      {/* Mobile: show active label name when filtering */}
      {activeLabel && (
        <button
          type="button"
          className="md:hidden inline-flex items-center gap-1 text-xs text-primary font-medium px-2 py-1 rounded-full bg-primary/10 border border-primary/30"
          onClick={() => onSelect('all')}
        >
          <AutoLabelBadge
            label={{ id: activeLabel.id, name: activeLabel.name, color: activeLabel.color ?? null }}
            className=""
          />
        </button>
      )}

      {/* Desktop: inline horizontal scroll */}
      <div className="hidden md:flex flex-nowrap gap-1 overflow-x-auto scrollbar-none pb-1 -mb-1">
        {renderFilterButton('all', 'all', null)}
        {noLabelOption ? renderFilterButton(noLabelOption.id, noLabelOption.name, noLabelOption.color) : null}
        {labels.length > 0 && <div className="w-px h-6 bg-border mx-1 my-auto shrink-0" />}
        {labels.map((label) => renderFilterButton(label.id, label.name, label.color))}
      </div>
    </>
  );
}
